import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Tooltip } from 'antd';
import {
  AlarmClock,
  CircleHelp,
  Hourglass,
  LucideIcon,
  Mail,
  MessagesSquare,
  Slack,
} from 'lucide-react';
import styled from 'styled-components';

import { TagTone, tagToneColors } from 'components/constants/tagTones';

/**
 * Значки коммуникаций (ТЗ 0096): иконка + число вместо цветных точек, которые
 * никто не запоминал. Один компонент на рейку Коммуникатора, строки студентов,
 * переключатель Slack | Почта и шапку Таск-трекера — и легенда ниже берёт
 * иконки и тона из ТЕХ ЖЕ карт, чтобы не разойтись с тем, что объясняет.
 *
 *  - `mail` / `slack` — непрочитанные по каналу;
 *  - `unread` — непрочитанные без разбивки (рейка, пока сервер не отдаёт
 *    раздельные счётчики; придут — передать `mail` + `slack` вместо него);
 *  - `waiting` — ждёт ответа: число диалогов (по умолчанию) или студентов
 *    (`waitingOf="students"`, рейка групп) либо флаг без числа;
 *  - `overdue` — просроченные задачи.
 *
 * Нулевые значки не рисуются; если рисовать нечего — компонент пустой.
 */
export type TCommsCounterKind =
  | 'mail'
  | 'slack'
  | 'unread'
  | 'waiting'
  | 'overdue';

export const COMMS_COUNTER_KINDS: readonly TCommsCounterKind[] = [
  'mail',
  'slack',
  'unread',
  'waiting',
  'overdue',
];

export const COMMS_COUNTER_ICONS: Readonly<
  Record<TCommsCounterKind, LucideIcon>
> = {
  mail: Mail,
  slack: Slack,
  unread: MessagesSquare,
  waiting: Hourglass,
  overdue: AlarmClock,
};

const COMMS_COUNTER_TONES: Readonly<Record<TCommsCounterKind, TagTone>> = {
  mail: 'info',
  slack: 'info',
  unread: 'info',
  waiting: 'warn',
  overdue: 'danger',
};

export interface ICommsCountersProps {
  mail?: number;
  slack?: number;
  unread?: number;
  /** Число — сколько ждут ответа; `true` — «есть», без числа. */
  waiting?: number | boolean;
  /**
   * Что считает `waiting` (перепроверка F40): `dialogs` — диалоги (строка
   * студента, переключатель Slack | Почта, плашка таск-трекера), `students`
   * — студенты (рейка групп). Одна подпись «Ждут ответа: N» на двух разных
   * величинах читалась как расхождение чисел.
   */
  waitingOf?: TWaitingUnit;
  overdue?: number;
  /** Плотный вариант для рейки и строк списка. */
  compact?: boolean;
  /**
   * Нативный `title` вместо antd Tooltip. Для длинных списков (рейка групп,
   * ростер): по Tooltip на каждый чип каждой строки — самая дорогая часть
   * рейки (замер ТЗ 0008). Смысл тот же, `aria-label` остаётся.
   */
  nativeTooltips?: boolean;
}

export type TWaitingUnit = 'dialogs' | 'students';

type TChipSize = { $compact: boolean };

const Row = styled.span<TChipSize>`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ $compact }) => ($compact ? 4 : 6)}px;
  flex-shrink: 0;
`;

const Chip = styled.span<TChipSize & { $tone: TagTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? 3 : 4)}px;
  padding: ${({ $compact }) => ($compact ? '1px 6px' : '2px 8px')};
  border-radius: 999px;
  font-size: ${({ $compact }) => ($compact ? 11 : 12)}px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  background: ${({ $tone }) => tagToneColors($tone).bg};
  color: ${({ $tone }) => tagToneColors($tone).fg};

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

const isShown = (value: number | boolean | undefined): boolean =>
  typeof value === 'boolean' ? value : (value ?? 0) > 0;

/** i18n-ключ значка: у «ждут ответа» по студентам — свой (F40). */
const counterKey = (kind: TCommsCounterKind, waitingOf: TWaitingUnit): string =>
  kind === 'waiting' && waitingOf === 'students' ? 'waitingStudents' : kind;

/** Подпись значка — и для tooltip, и для `aria-label`. */
export const useCommsCounterLabel = () => {
  const { t } = useTranslation();
  return (
    kind: TCommsCounterKind,
    value: number | boolean,
    waitingOf: TWaitingUnit = 'dialogs',
  ): string =>
    typeof value === 'boolean'
      ? t(`commsCounters.flag.${counterKey(kind, waitingOf)}`)
      : t(`commsCounters.label.${counterKey(kind, waitingOf)}`, { n: value });
};

export const CommsCounters = memo(
  ({
    mail,
    slack,
    unread,
    waiting,
    overdue,
    waitingOf = 'dialogs',
    compact = false,
    nativeTooltips = false,
  }: ICommsCountersProps) => {
    const label = useCommsCounterLabel();
    const values: Record<TCommsCounterKind, number | boolean | undefined> = {
      mail,
      slack,
      unread,
      waiting,
      overdue,
    };
    const shown = COMMS_COUNTER_KINDS.filter((kind) => isShown(values[kind]));
    if (!shown.length) return null;

    const iconSize = compact ? 11 : 13;
    return (
      <Row $compact={compact}>
        {shown.map((kind) => {
          const value = values[kind] as number | boolean;
          const text = label(kind, value, waitingOf);
          const Icon = COMMS_COUNTER_ICONS[kind];
          const chip = (
            <Chip
              key={kind}
              role="img"
              aria-label={text}
              title={nativeTooltips ? text : undefined}
              $compact={compact}
              $tone={COMMS_COUNTER_TONES[kind]}
            >
              <Icon size={iconSize} strokeWidth={2} aria-hidden="true" />
              {typeof value === 'number' ? value : null}
            </Chip>
          );
          return nativeTooltips ? (
            chip
          ) : (
            <Tooltip key={kind} title={text}>
              {chip}
            </Tooltip>
          );
        })}
      </Row>
    );
  },
);

CommsCounters.displayName = 'CommsCounters';

/* --- Легенда «Что значат значки?» --- */

const LegendTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--k-fg-475569);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--k-bg-eff2f7);
    color: var(--k-fg-0f172a);
  }

  svg {
    display: block;
  }
`;

const LegendList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LegendItem = styled.li`
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: start;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
  color: var(--k-fg-334155);
`;

const LegendNote = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--k-fg-64748b);
`;

/** Кнопка-«?» с поповером, объясняющим каждый значок. */
export const CommsCountersLegend = memo(() => {
  const { t } = useTranslation();
  const content = (
    <>
      <LegendList>
        {COMMS_COUNTER_KINDS.map((kind) => {
          const Icon = COMMS_COUNTER_ICONS[kind];
          return (
            <LegendItem key={kind}>
              <Chip $compact $tone={COMMS_COUNTER_TONES[kind]} aria-hidden>
                <Icon size={11} strokeWidth={2} aria-hidden="true" />3
              </Chip>
              <span>{t(`commsCounters.legend.${kind}`)}</span>
            </LegendItem>
          );
        })}
      </LegendList>
      <LegendNote>{t('commsCounters.legend.zeroHidden')}</LegendNote>
    </>
  );
  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      title={t('commsCounters.legend.title')}
      content={content}
    >
      <LegendTrigger
        type="button"
        aria-label={t('commsCounters.legend.title')}
        title={t('commsCounters.legend.title')}
      >
        <CircleHelp size={18} strokeWidth={2} aria-hidden="true" />
      </LegendTrigger>
    </Popover>
  );
});

CommsCountersLegend.displayName = 'CommsCountersLegend';
